import { ContactRequirements } from "types/ContactRequirements";
import { ContactData } from "components/utils/contacts";
import type { JSONSchema7 } from "json-schema";

export enum ContactorSearchColumnKeys {
    fullName = "fullName",
    fullAddress = "fullAddress",
    phone = "phone",
    email = "email",
    technologies = "technologies",
    services = "services",
}

export enum ContactTypeCode {
    Contractor = 19,
    Primary = 17,
    Premise = 26,
}

export interface FormState {
    formData: any;
    schema: JSONSchema7;
}

export interface ContactInboundData {
    contactType: string;
    contactNumber?: string;
    contactTitle?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    address?: string;
    address_cont?: string;
    lot?: string;
    city?: string;
    state?: string;
    zip?: string;
    shortZip?: string;
    phone?: string;
    cell?: string;
    fax?: string;
    email?: string;
    accountNumber?: string;
    taxId?: string;
    premiseId?: string;
    meterid?: string;
    workflowTargetGroupId?: number;
    workflowTargetGroupName?: string;
    origId?: string;
}

export interface ContractorsResponse {
    totalRecords: number;
    remainingRecords: number;
    items: ContractorsResponseItem[];
}

export interface ContractorsResponseItemBase {
    contactNumber: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string | null;
    email?: string | null;
    technologies?: string;
    services?: string;
    url?: string | null;
    description?: string | null;
    fullName?: string;
    fullAddress?: string;
}

export interface ContractorsResponseItem extends ContractorsResponseItemBase {
    lat?: number | undefined | null;
    lng?: number | undefined | null;
    href?: string | null;
}

export interface ContactFormData {
    origId?: string;
    storedContactNumber?: string;
    contactType?: string;
    contactTitle?: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    premiseId?: string;
    meterId?: string;
    acct_number?: string;
    address?: string;
    address_cont?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    cell?: string;
    fax?: string;
    email?: string;
    taxId?: string;
    lot?: string;
    saveContact?: boolean;
    copyContact?: boolean;
}

export interface ContractorFormData {
    contactType?: string;
    contactTitle?: string;
    company?: string;
    firstName?: string;
    lastName?: string;
    technologies?: string;
    services?: string;
    premiseId?: string;
    meterId?: string;
    acct_number?: string;
    lot?: string;
    address?: string;
    address_cont?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string;
    cell?: string;
    fax?: string;
    email?: string;
    taxId?: string;
    contactNumber?: string;
    origId?: string;
    storedContactNumber?: string;
    saveContact?: boolean;
}

export interface BaseContactFormProps {
    errors: any;
    idPrefix?: string;
    requirements: ContactRequirements;
    noTitle?: boolean;
    hide?: boolean;
    requiredFields?: string[];
    hiddenFields?: string[];
    readonly?: boolean;
    onChange: (form: any) => void;
    onClearForm: () => void;
    onSaveClick?: () => void;
    onCopyContact?: () => void;
    saveContact?: boolean;
    onUseCustomerContact?: (contact: ContactInboundData | undefined) => void;
    contactType?: ContactTypeCode;
}

export interface PremiseContactFormProps extends BaseContactFormProps {
    formData?: ContactFormData;
}

export interface PrimaryContactFormProps extends BaseContactFormProps {
    formData?: ContactFormData;
    onCopyContact?: () => void;
}

export interface ContractorContactFormProps extends BaseContactFormProps {
    formData?: ContractorFormData;
    requiredFields?: string[];
    hiddenFields?: string[];
    hide?: boolean;
    isAdditionalContact?: boolean;
    applicationNumber: string;
}

export interface ExistingContactProps {
    contact?: ContactFormData | ContractorFormData;
    readonly?: boolean;
    hiddenFields?: string[];
    lotLabel: string;
    onClear?: () => void;
    onUseCustomerContact?: (contact?: ContactInboundData) => void;
    customerContacts?: ContactInboundData[];
}

export interface ContactHeaderProps {
    hideStored?: boolean;
    onCopyContact?: () => void;
    formData?: ContactFormData | ContractorFormData;
    customerContacts: ContactInboundData[];
    onUseCustomerContact?: (contact: any) => void;
    requirements?: ContactRequirements;
    contactType?: ContactTypeCode;
    idPrefix: string;
}

export type ContactValidationResult = any | undefined;

export interface ApplicationContactsWidgetRef {
    validate: () => ApplicationContactsWidgetValidationResult;
    validateDataFormat: () => ApplicationContactsWidgetValidationResult;
    fieldNumber: string;
    getSubmitValues: () => ContactData[];
}

export interface ApplicationContactsWidgetValidationResult {
    primaryContactErrors?: ContactValidationResult;
    premiseContactErrors?: ContactValidationResult;
    contractorContactErrors?: ContactValidationResult;
}

export interface ApplicationContactsWidgetValidationParams {
    primaryContactData: ContactFormData;
    premiseContactData: ContactFormData;
    contractorContactData: ContractorFormData;
    requirements: ContactRequirements;
    contractorLabel: string;
}

export interface ContactFieldsProps {
    idPrefix: string;
    formData?: ContactFormData | ContractorFormData;
    onChange: (patch: Partial<ContactFormData>) => void;
    requiredFields: string[];
    errors: ContactValidationResult;
    hiddenFields: string[];
    onClearForm?: () => void;
    lotLabel: string;
    needsFirstAndLastOrCompany: boolean;
}

export interface ContractorsResponseItemBase {
    contactNumber: string;
    firstName?: string;
    lastName?: string;
    company?: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
    phone?: string | null;
    email?: string | null;
    technologies?: string;
    services?: string;
    url?: string | null;
    description?: string | null;
    fullName?: string;
    fullAddress?: string;
}

export interface ContractorsResponseItemData extends ContractorsResponseItemBase {
    lat?: string | number;
    lng?: string | number;
}

export interface ContractorsResponseItem extends ContractorsResponseItemBase {
    lat?: number | undefined | null;
    lng?: number | undefined | null;
    href?: string | null;
}
